import { isChunkLoadError } from './chunkErrorHandling'
import { getAppMode } from './domain'
import {
  isBrowserStorageLossError,
  isExpectedAxiosClientError,
  isFirebaseNetworkError,
  isNetworkError,
} from './errorHandling'

/**
 * Returns true when the browser took the origin's IndexedDB away *and* this app
 * had no first-party database open, so the rejection can only have come from
 * Firebase's auth-persistence or heartbeat store.
 *
 * The mode gate is the whole rule, because the sentence cannot carry the
 * answer: the rejection reaches `beforeSend` with no stack frames, and on
 * `app.scrivio.nl` the same wording is what an in-flight `RecordingDatabase`
 * write rejects with when the connection dies mid-consult —
 * `useSessionRecovery` routes those to Sentry on purpose, because that is the
 * ADR-033 local-audio gap insurance not being written. Dropping the sentence
 * globally would trade a benign portal event for a real recording defect going
 * dark (SCRIVIO-FE-F9).
 *
 * `!== 'behandelaar'` is deliberately the negation of `useRecoveryNotification`'s
 * own gate: that composable is the only thing that opens `scrivio-recordings`,
 * and it opens it exactly when the mode *is* `behandelaar` (SCRIVIO-FE-F0). The
 * two conditions have to stay each other's complement — a portal that starts
 * recording is a portal that must stop dropping these.
 */
function isBenignStorageLoss(error: unknown): boolean {
  return isBrowserStorageLossError(error) && getAppMode() !== 'behandelaar'
}

/**
 * Decides whether a Sentry event for `error` is known-benign and must be
 * dropped before it leaves the browser; `beforeSend` is a thin wrapper.
 *
 * `captureConsoleIntegration` promotes every `console.error` to a Sentry event,
 * so any call site that logs a transient failure pages someone. Call sites are
 * expected to guard themselves, but a new unguarded catch block re-opens the
 * same Sentry group. These are the defence-in-depth rules.
 *
 * Trade-off: dropping `ERR_NETWORK` also hides a genuine post-deploy CORS/DNS
 * misconfiguration, which reaches the browser as a response-less network
 * failure indistinguishable from a phone losing signal. The rule is drawn at the
 * response: an error that carries an HTTP response (5xx, parse errors) still
 * flows through, an error that carries none does not — including
 * `ECONNABORTED`/`ETIMEDOUT`, which without a configured axios deadline is a
 * browser-level transport abort rather than a slow backend (SCRIVIO-FE-BF).
 */
export function shouldDropSentryEvent(error: unknown): boolean {
  return (
    isChunkLoadError(error) ||
    // Handled 4xx raised when dashboard composables fetch from endpoints the
    // user has no permission for.
    isExpectedAxiosClientError(error) ||
    // A dropped or aborted request carries no response, so the status-based
    // guard above misses it (SCRIVIO-FE-A4, SCRIVIO-FE-5W).
    isNetworkError(error) ||
    // A Firebase token refresh that loses the network rejects with
    // `auth/network-request-failed` and no HTTP response (SCRIVIO-FE-5E).
    isFirebaseNetworkError(error) ||
    // The browser took the origin's IndexedDB away mid-session, in an app that
    // opens no database of its own. Firebase logs its own two failures at
    // `warn`; the sibling rejection escaping unhandled arrives here with no
    // stack frames (SCRIVIO-FE-F0, SCRIVIO-FE-F9).
    isBenignStorageLoss(error)
  )
}
